import api from './api';
import { GlossaryTerm, CorpusSector } from '../types';

export interface GlossaryTermWithSector extends GlossaryTerm {
  sector: {
    id: number;
    name: string;
    code: string;
    icon?: string;
    color?: string;
  };
}

export interface TermsBySectorResponse {
  success: boolean;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasMore: boolean;
  terms: GlossaryTermWithSector[];
}

export interface AllTermsResponse {
  success: boolean;
  total: number;
  termsBySector: {
    sector: CorpusSector;
    terms: GlossaryTermWithSector[];
  }[];
}

export interface CreateTermData {
  term: string;
  acronym?: string;
  definition: string;
  simplifiedDefinition?: string;
  priority: 'ALTA' | 'MEDIA' | 'BAJA';
  sectorId: number;
  mcerlLevel: 'B2' | 'C1' | 'C2';
  examples?: string[];
  synonyms?: string[];
}

export interface UpdateTermData {
  term?: string;
  acronym?: string;
  definition?: string;
  simplifiedDefinition?: string;
  priority?: 'ALTA' | 'MEDIA' | 'BAJA';
  sectorId?: number;
  mcerlLevel?: 'B2' | 'C1' | 'C2';
  examples?: string[];
  synonyms?: string[];
}

export interface ImportTermsData {
  sectorId: number;
  terms: CreateTermData[];
  format?: 'json' | 'txt';
}

export interface ImportResultsResponse {
  success: boolean;
  message: string;
  results: {
    created: number;
    skipped: number;
    errors: string[];
  };
}

export interface GlossaryStats {
  success: boolean;
  stats: {
    total: number;
    bySector: {
      sector: CorpusSector;
      count: number;
    }[];
    byPriority: {
      priority: 'ALTA' | 'MEDIA' | 'BAJA';
      count: number;
    }[];
    byMcerlLevel: {
      mcerlLevel: 'B2' | 'C1' | 'C2';
      count: number;
    }[];
  };
}

export const glossaryService = {
  /**
   * Get all terms for a specific sector
   */
  async getTermsBySector(
    sectorId: number,
    params?: {
      search?: string;
      priority?: 'ALTA' | 'MEDIA' | 'BAJA';
      mcerlLevel?: 'B2' | 'C1' | 'C2';
      page?: number;
      limit?: number;
    }
  ): Promise<TermsBySectorResponse> {
    const response = await api.get<TermsBySectorResponse>(`/glossary/sectors/${sectorId}`, {
      params,
    });
    return response.data;
  },

  /**
   * Get all terms grouped by sector
   */
  async getAllTerms(params?: { search?: string }): Promise<AllTermsResponse> {
    const response = await api.get<AllTermsResponse>('/glossary/terms', { params });
    return response.data;
  },

  /**
   * Create a new glossary term
   */
  async createTerm(data: CreateTermData): Promise<{
    success: boolean;
    message: string;
    term: GlossaryTermWithSector;
  }> {
    const response = await api.post('/glossary/terms', data);
    return response.data;
  },

  /**
   * Update a glossary term
   */
  async updateTerm(
    id: number,
    data: UpdateTermData
  ): Promise<{
    success: boolean;
    message: string;
    term: GlossaryTermWithSector;
  }> {
    const response = await api.put(`/glossary/terms/${id}`, data);
    return response.data;
  },

  /**
   * Delete a glossary term
   */
  async deleteTerm(id: number): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await api.delete(`/glossary/terms/${id}`);
    return response.data;
  },

  /**
   * Bulk import glossary terms
   */
  async importTerms(data: ImportTermsData): Promise<ImportResultsResponse> {
    const response = await api.post<ImportResultsResponse>('/glossary/import', data);
    return response.data;
  },

  /**
   * Export glossary terms as JSON or TXT
   */
  async exportTerms(params?: {
    sectorId?: number;
    format?: 'json' | 'txt';
  }): Promise<Blob> {
    const response = await api.get('/glossary/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Get glossary statistics
   */
  async getStats(): Promise<GlossaryStats> {
    const response = await api.get<GlossaryStats>('/glossary/stats');
    return response.data;
  },

  /**
   * Helper function to download exported file
   */
  downloadExportedFile(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};
